<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { isFixedWidthArt, type RichOrigin, toRichBlocks } from "./render";

/**
 * Renders one piece of agent or contact text, richly.
 *
 * Mermaid is imported DYNAMICALLY and only when a diagram is actually present. It is
 * the single largest dependency in the app, and a chat that never draws a diagram
 * should not pay for it — which is also why blocks are split upstream rather than
 * here.
 */
const props = withDefaults(defineProps<{ text: string; origin?: RichOrigin }>(), {
	origin: "agent",
});

const blocks = computed(() => toRichBlocks(props.text, props.origin));
const diagrams = ref<Record<number, string>>({});
const failed = ref<Record<number, string>>({});

/**
 * The code block's toolbar: copy, and wrap.
 *
 * Delegated from the container rather than bound per button: the HTML is
 * replaced wholesale on every render, and listeners attached to the old nodes
 * would be dropped silently on the first streamed update.
 *
 * WRAP is a class on the block and not a preference: two code blocks in one
 * answer are two different shapes — a one-line shell command wants to stay on
 * one line, and a 200-character SQL statement in a 24rem dock has to fold or its
 * own ending is off screen. A global switch would get the second one wrong every
 * time it got the first one right.
 */
async function onClick(event: MouseEvent): Promise<void> {
	const target = event.target as HTMLElement | null;

	const wrap = target?.closest("[data-rich-wrap]");
	if (wrap instanceof HTMLElement) {
		wrap.closest(".rich-code")?.classList.toggle("rich-code--wrap");
		// A folded block no longer has a width to fit into, so whatever we shrank
		// it to is given back — folding is the person's own answer to the same
		// problem, and two answers at once is unreadably small text that also wraps.
		fitArt();
		return;
	}

	const button = target?.closest("[data-rich-copy]");
	if (!(button instanceof HTMLElement)) return;

	// From the BLOCK and not from the sibling: the button lives inside a toolbar
	// now, so `parentElement` is the bar and the `<pre>` is one level further out.
	const code = button.closest(".rich-code")?.querySelector("pre")?.textContent ?? "";
	if (!code) return;

	try {
		await navigator.clipboard.writeText(code);
		button.textContent = "copiado";
		setTimeout(() => {
			button.textContent = "copiar";
		}, 1500);
	} catch {
		// Clipboard access is refused in some embedded contexts. The code is on
		// screen and selectable, so this is a lost convenience, not a failure.
	}
}

const root = ref<HTMLElement | null>(null);

/**
 * The floor, in px. Nothing drawn with characters survives being smaller than
 * this — it becomes a texture — so a drawing that would need less keeps its
 * scroll instead.
 *
 * EIGHT and not ten: the trees Ben draws run about 70 columns, which needs 8.1px
 * in the ~342px a 390px phone leaves after the page and the block's own padding.
 * A floor of 10 would refuse exactly the case this exists for and change nothing
 * on screen. At a phone's pixel density 8px is small but legible, and the shape —
 * which is the whole reason someone draws a tree — is finally all there.
 */
const MIN_ART_FONT_PX = 8;

/**
 * Shrinks a DRAWING until the whole of it is on screen.
 *
 * A code block scrolls sideways, which is right for code and wrong for a tree:
 * the branches that fall off the right edge are the half that says what the thing
 * contains, and no one scrolls a picture back and forth to assemble it mentally.
 * Ben's inventory tree on a phone showed "3 agente", "37 funçõ", "4 automa" — the
 * answer was there and unreadable.
 *
 * Only for art (see `isFixedWidthArt`), never for code: a long SQL line reads fine
 * one line at a time and shrinking it to 9px would help nobody.
 *
 * The size is set on the `<code>`, where the stylesheet puts it, and is always
 * cleared before measuring — otherwise the second pass would fit the block to the
 * size the first pass chose and creep smaller on every keystroke of a stream.
 */
function fitArt(): void {
	const host = root.value;
	if (!host) return;

	for (const block of host.querySelectorAll<HTMLElement>(".rich-code")) {
		const pre = block.querySelector("pre");
		const code = pre?.querySelector("code") ?? pre;
		if (!pre || !code) continue;

		code.style.fontSize = "";
		block.classList.remove("rich-code--art");
		if (block.classList.contains("rich-code--wrap")) continue;
		if (!isFixedWidthArt(pre.textContent ?? "")) continue;

		// The class first and the measurement after: it narrows the block's own
		// side padding, and those twelve pixels are worth about half a point of
		// type — the difference between the 70-column tree landing at 8.4px and
		// hitting the floor 5px short of fitting.
		block.classList.add("rich-code--art");

		const style = getComputedStyle(pre);
		const padding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
		const available = pre.clientWidth - padding;
		const drawn = pre.scrollWidth - padding;
		// One pixel of slack: a fractional width rounds up and would "overflow" a
		// drawing that already fits.
		if (!(available > 0) || !(drawn > available + 1)) continue;

		const base = Number.parseFloat(getComputedStyle(code).fontSize);
		if (!(base > 0)) continue;
		code.style.fontSize = `${Math.max(MIN_ART_FONT_PX, (base * available) / drawn).toFixed(2)}px`;
	}
}

/**
 * Refits when the COLUMN changes width — the dock being dragged, a phone turned.
 * Width only: fitting changes the block's height, and a height-triggered refit
 * would be a loop with the observer on one end and the font size on the other.
 */
let lastWidth = 0;
const observer =
	typeof ResizeObserver === "undefined"
		? null
		: new ResizeObserver((entries) => {
				const width = entries[0]?.contentRect.width ?? 0;
				if (Math.abs(width - lastWidth) < 1) return;
				lastWidth = width;
				fitArt();
			});

onMounted(() => {
	if (root.value && observer) observer.observe(root.value);
});

let disposed = false;
onUnmounted(() => {
	disposed = true;
	observer?.disconnect();
});

// After the DOM has the new HTML, not before: the block is replaced wholesale on
// every streamed update and there is nothing to measure until it is on the page.
watch(
	blocks,
	() => {
		void nextTick(fitArt);
	},
	{ immediate: true, flush: "post" },
);

watch(
	blocks,
	async (current) => {
		const pending = current
			.map((block, index) => ({ block, index }))
			.filter(({ block }) => block.kind === "mermaid");
		if (pending.length === 0) return;

		const mermaid = (await import("mermaid")).default;
		mermaid.initialize({
			startOnLoad: false,
			// Follows the app's own theme rather than mermaid's default, which is a
			// bright white card in the middle of a dark chat.
			theme: document.documentElement.dataset.theme === "dark" ? "dark" : "default",
			securityLevel: "strict",
			fontFamily: "inherit",
		});

		for (const { block, index } of pending) {
			try {
				// A unique id per render: mermaid keys its internal state by it, and a
				// repeated id silently returns the previous diagram.
				const { svg } = await mermaid.render(
					`d${index}-${Math.random().toString(36).slice(2)}`,
					block.content,
				);
				if (!disposed) diagrams.value = { ...diagrams.value, [index]: svg };
			} catch (e) {
				// A malformed diagram shows its source, not a blank space: the source is
				// what the person needs to fix it.
				if (!disposed) {
					failed.value = { ...failed.value, [index]: e instanceof Error ? e.message : String(e) };
				}
			}
		}
	},
	{ immediate: true },
);
</script>

<template>
	<div ref="root" class="rich" @click="onClick">
		<template v-for="(block, index) in blocks" :key="index">
			<!-- eslint-disable-next-line vue/no-v-html -- sanitised in render.ts, per origin -->
			<div v-if="block.kind === 'html'" class="rich-html" v-html="block.content" />
			<figure v-else class="rich-diagram">
				<!-- eslint-disable-next-line vue/no-v-html -- mermaid output, securityLevel strict -->
				<div v-if="diagrams[index]" v-html="diagrams[index]" />
				<pre v-else-if="failed[index]" class="rich-diagram-failed">{{ block.content }}</pre>
				<div v-else class="rich-diagram-loading">drawing diagram…</div>
				<figcaption v-if="failed[index]">Invalid diagram: {{ failed[index] }}</figcaption>
			</figure>
		</template>
	</div>
</template>

<style scoped>
.rich {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	min-width: 0;
}

.rich-html {
	min-width: 0;
	line-height: 1.55;
	word-break: break-word;
}
/* Leitura (o chamador passa `font-serif text-[1.0625rem] leading-7`): a entrelinha
   é a de quem chamou, não a da conversa. */
.rich.font-serif .rich-html {
	line-height: inherit;
}

.rich-html :deep(p) {
	margin: 0 0 0.5rem;
}
.rich-html :deep(p:last-child) {
	margin-bottom: 0;
}
.rich-html :deep(strong) {
	font-weight: 600;
}
.rich-html :deep(ul),
.rich-html :deep(ol) {
	margin: 0 0 0.5rem;
	padding-left: 1.25rem;
}
/* O preflight do Tailwind zera o marcador: sem isto uma lista lia-se como parágrafos soltos. */
.rich-html :deep(ul) {
	list-style: disc;
}
.rich-html :deep(ol) {
	list-style: decimal;
}
.rich-html :deep(li) {
	margin: 0.125rem 0;
}
.rich-html :deep(h1),
.rich-html :deep(h2),
.rich-html :deep(h3),
.rich-html :deep(h4) {
	margin: 0.75rem 0 0.375rem;
	font-weight: 600;
	line-height: 1.3;
}
.rich-html :deep(h1) {
	font-size: 1.125rem;
}
.rich-html :deep(h2) {
	font-size: 1.0625rem;
}
.rich-html :deep(h3),
.rich-html :deep(h4) {
	font-size: 1rem;
}
.rich-html :deep(code) {
	font-family: var(--font-mono);
	font-size: 0.8125em;
	background: var(--color-bg-muted);
	border-radius: var(--radius-sm);
	padding: 0.1em 0.35em;
}
/* The toolbar sits over the block's top-right corner, out of the way of the
   first line, and only appears once there is a reason to reach for it. */
.rich-html :deep(.rich-code) {
	position: relative;
}
.rich-html :deep(.rich-code__bar) {
	position: absolute;
	top: 0.375rem;
	right: 0.375rem;
	z-index: 1;
	display: flex;
	align-items: center;
	gap: 0.25rem;
	opacity: 0;
	transition: opacity 0.12s ease;
}
.rich-html :deep(.rich-code__lang) {
	font-family: var(--font-mono);
	font-size: 0.625rem;
	text-transform: lowercase;
	color: var(--color-fg-subtle);
	padding-right: 0.125rem;
}
.rich-html :deep(.rich-code__btn) {
	font-family: var(--font-sans);
	border: 1px solid var(--color-border);
	border-radius: 9999px;
	background: var(--color-surface-raised);
	color: var(--color-fg-muted);
	font-size: 0.6875rem;
	padding: 0.125rem 0.375rem;
	cursor: pointer;
}
.rich-html :deep(.rich-code__btn:hover) {
	color: var(--color-fg);
}
.rich-html :deep(.rich-code:hover) .rich-code__bar,
.rich-html :deep(.rich-code__bar:focus-within) {
	opacity: 1;
}
/* Touch has no hover, and a button that never appears is a button that does not
   exist. So on touch the bar is always on — and therefore always ON TOP of the
   first line of the block, which on a phone is where a diagram's own title sits.
   It comes out of the corner and becomes a row of its own above the code: the
   pixels it costs are the pixels it was stealing. */
@media (hover: none) {
	.rich-html :deep(.rich-code__bar) {
		opacity: 1;
		position: static;
		justify-content: flex-end;
		padding-bottom: 0.25rem;
	}
}
/* A DRAWING gives back the side padding a code block keeps for comfort: on a
   phone those twelve pixels are two characters of the diagram. Set by `fitArt`,
   which measures with it already applied. */
.rich-html :deep(.rich-code--art) pre {
	padding-left: 0.375rem;
	padding-right: 0.375rem;
}
/* Folded on request: the block stops scrolling sideways and the longest line
   ends where the eye can still reach it. */
.rich-html :deep(.rich-code--wrap) pre,
.rich-html :deep(.rich-code--wrap) pre code {
	white-space: pre-wrap;
	word-break: break-word;
	overflow-wrap: anywhere;
}

.rich-html :deep(pre) {
	/* Code and tables scroll INSIDE their block; the page itself must never scroll
	   sideways, which is what makes long output survive a phone screen. */
	overflow-x: auto;
	background: var(--color-bg-muted);
	border: 1px solid var(--color-border);
	border-radius: var(--radius-lg);
	padding: 0.625rem 0.75rem;
	margin: 0 0 0.5rem;
}
.rich-html :deep(pre code) {
	background: none;
	padding: 0;
	font-size: 0.8125rem;
	line-height: 1.5;
}
.rich-html :deep(blockquote) {
	margin: 0 0 0.5rem;
	padding-left: 0.75rem;
	border-left: 2px solid var(--color-border-strong);
	color: var(--color-fg-muted);
}
.rich-html :deep(a) {
	color: var(--color-accent);
	text-decoration: underline;
	text-underline-offset: 2px;
}
.rich-html :deep(img) {
	max-width: 100%;
	height: auto;
	border-radius: var(--radius-md);
	display: block;
}
/* The wrapper scrolls; the table takes its natural width. Reversing the two is
   what crushed a "Código" header into two lines over a 6px column. */
.rich-html :deep(.rich-table-scroll) {
	overflow-x: auto;
	max-width: 100%;
	margin: 0 0 0.5rem;
}
/* Quem lê em serifa (Arquivos, Manual, Missões) passa `font-serif` na raiz; a
   tabela continua sans — serifa em tabela densa não se lê (padrão Claude, 25/10). */
.rich-html :deep(table) {
	font-family: var(--font-sans);
	border-collapse: collapse;
	font-size: 0.8125rem;
	margin: 0;
	/* Fits the column when it can, scrolls in the wrapper when it can't. Era
	   `max-content`: uma célula com uma frase virava uma linha só, e a tabela de
	   uma pesquisa saía com metros de largura no telefone (dono, 25/10). */
	width: auto;
	max-width: none;
}
.rich-html :deep(td) {
	/* Quebra entre PALAVRAS, nunca dentro delas — o `anywhere` herdado da conversa
	   é o que fazia "Revisão" virar "Revisã / o" — e nenhuma coluna fica magra. */
	overflow-wrap: normal;
	word-break: normal;
	min-width: 7rem;
}
.rich-html :deep(td code),
.rich-html :deep(th code) {
	/* An identifier broken across two lines is no longer an identifier. */
	white-space: nowrap;
}
.rich-html :deep(th) {
	/* A header never wraps: it is short, and wrapping it is what looks broken. */
	white-space: nowrap;
}
/* Tabela quieta: só a linha fina entre as filas, sem grade nem cabeçalho pintado. */
.rich-html :deep(th),
.rich-html :deep(td) {
	border-bottom: 1px solid var(--color-border);
	padding: 0.375rem 0.625rem;
	text-align: left;
}
.rich-html :deep(th) {
	color: var(--color-fg-muted);
	font-weight: 500;
}
.rich-html :deep(hr) {
	border: none;
	border-top: 1px solid var(--color-border);
	margin: 0.75rem 0;
}

.rich-diagram {
	font-family: var(--font-sans);
	margin: 0;
	overflow-x: auto;
	background: var(--color-bg-muted);
	border: 1px solid var(--color-border);
	border-radius: var(--radius-xl);
	padding: 0.75rem;
}
.rich-diagram :deep(svg) {
	max-width: 100%;
	height: auto;
}
.rich-diagram-loading {
	font-size: 0.8125rem;
	color: var(--color-fg-muted);
}
.rich-diagram-failed {
	margin: 0;
	font-family: var(--font-mono);
	font-size: 0.75rem;
	white-space: pre-wrap;
}
.rich-diagram figcaption {
	margin-top: 0.5rem;
	font-size: 0.75rem;
	color: var(--color-danger);
}
</style>
